import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BALANCE_SECTIONS, aggregateFinancialMonths, type BalanceItem, type BalanceSection, type ExpenseCategory, type FinancialGranularity } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchFinancials, fetchLosses, saveBalanceItems, type FinancialsResponse, type LossesResponse } from "../services/expensesService";
import { Dialog, ErrorBox, Field, Notice, Page, Spinner, Tabs } from "../components/ui";
import { categoryLabel, currentMonth, downloadText, lang as langOf, money, monthLabel, shiftMonth } from "../lib/format";
import { applyMonthRange, isMonth } from "../lib/monthRange";
import { alignPeriodStart, cashFlowRows, incomeStatementRows, lossesByPeriod, statementCsv, type StatementRow } from "../lib/financialRows";
import { ui } from "../theme";

// Estados financieros: los tres estados básicos, calculados con los mismos
// movimientos que el resto de Gastos.
//
//   · Estado de resultados: ¿hubo ganancia o pérdida en el periodo?
//   · Balance general: qué se tiene, qué se debe y qué queda, a una fecha.
//   · Flujo de efectivo: cómo entró y salió el dinero (operación, inversión y
//     financiamiento).

type View = "income" | "balance" | "cashflow";
const GRANULARITIES: FinancialGranularity[] = ["month", "quarter", "year"];
const DEFAULT_MONTHS = 12;

type ItemDraft = { id?: string; section: BalanceSection; label: string; amount: string; date: string };

export default function FinancialStatementsSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const lang = i18n.language;
  const [view, setView] = useState<View>("income");
  const [granularity, setGranularity] = useState<FinancialGranularity>("month");
  const [range, setRange] = useState(() => ({ from: shiftMonth(currentMonth(), -(DEFAULT_MONTHS - 1)), to: currentMonth() }));
  // Lo tecleado a medias en <input type="month"> vive aquí hasta ser un mes válido.
  const [draft, setDraft] = useState(range);
  useEffect(() => setDraft(range), [range]);
  const [data, setData] = useState<FinancialsResponse | null>(null);
  const [losses, setLosses] = useState<LossesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [items, setItems] = useState<ItemDraft[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);

  // Trimestres y años completos: el rango arranca al inicio del periodo.
  const from = alignPeriodStart(range.from, granularity);

  useEffect(() => {
    let alive = true;
    if (reload === 0) setData(null);
    Promise.all([fetchFinancials({ from, to: range.to }), fetchLosses({ from, to: range.to }).catch(() => null)])
      .then(([fin, loss]) => {
        if (!alive) return;
        setData(fin);
        setLosses(loss);
        setError(null);
      })
      .catch((err) => alive && setError(apiErrorMessage(err) || "load"));
    return () => {
      alive = false;
    };
  }, [from, range.to, reload]);

  const changeRange = (change: { from?: string; to?: string }) => {
    setDraft((d) => ({ ...d, ...change }));
    const next = applyMonthRange(range, change);
    if (next.from !== range.from || next.to !== range.to) setRange(next);
  };

  const periods = useMemo(() => (data ? aggregateFinancialMonths(data.months, granularity) : []), [data, granularity]);
  const periodLabel = (key: string) => {
    if (isMonth(key)) return monthLabel(key, lang);
    const quarter = /^(\d{4})-Q(\d)$/.exec(key);
    return quarter ? `${langOf(lang) === "en" ? "Q" : "T"}${quarter[2]} ${quarter[1]}` : key;
  };
  const labels = periods.map((p) => periodLabel(p.period));

  const rows = useMemo<StatementRow[]>(() => {
    if (!data) return [];
    if (view === "cashflow") return cashFlowRows(periods, data.openingCash);
    return incomeStatementRows(periods, (cat: ExpenseCategory) => categoryLabel(cat, lang), losses ? lossesByPeriod(losses.months, granularity) : null);
  }, [data, view, periods, losses, granularity, lang]);

  const m = (value: number | null | undefined) => money(value, "MXN", lang);
  const rowLabel = (row: StatementRow) => row.label ?? t(`expenses.financials.row.${row.key}`);
  const cell = (row: StatementRow, value: number | null) => (value == null ? "—" : row.percent ? `${Math.round(value * 1000) / 10}%` : m(value));

  const openItems = () => {
    setItemsError(null);
    setItems((data?.items || []).map((it) => ({ id: it.id, section: it.section, label: it.label, amount: String(it.amount), date: it.date || "" })));
  };
  const saveItems = async () => {
    if (!items) return;
    setBusy(true);
    setItemsError(null);
    try {
      await saveBalanceItems(items.map((it) => ({ id: it.id || "", section: it.section, label: it.label.trim(), amount: Number(it.amount), date: it.date || null })) as BalanceItem[]);
      setItems(null);
      setReload((n) => n + 1);
    } catch (err) {
      setItemsError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setBusy(false);
    }
  };
  const itemsValid = (items || []).every((it) => it.label.trim() && it.amount.trim() !== "" && Number.isFinite(Number(it.amount)));

  const b = data?.balance;
  const manualRows = (list: BalanceItem[]) =>
    list.map((it) => (
      <BalanceRow key={it.id} label={it.label} value={m(it.amount)} tag={t("expenses.financials.manualTag")} />
    ));

  return (
    <Page
      title={t("expenses.menu.cash")}
      subtitle={t("expenses.financials.subtitle")}
      actions={
        <>
          <input type="month" aria-label={t("expenses.financials.from")} className={`${c.inputBase} w-auto`} value={draft.from} max={draft.to} onChange={(e) => changeRange({ from: e.target.value })} onBlur={() => setDraft(range)} />
          <input type="month" aria-label={t("expenses.financials.to")} className={`${c.inputBase} w-auto`} value={draft.to} max={currentMonth()} onChange={(e) => changeRange({ to: e.target.value })} onBlur={() => setDraft(range)} />
          <select aria-label={t("expenses.financials.granularity")} className={`${c.inputBase} w-auto`} value={granularity} onChange={(e) => setGranularity(e.target.value as FinancialGranularity)}>
            {GRANULARITIES.map((g) => (
              <option key={g} value={g}>
                {t(`expenses.financials.granularityOption.${g}`)}
              </option>
            ))}
          </select>
        </>
      }
    >
      <Tabs
        label={t("expenses.menu.cash")}
        value={view}
        onChange={setView}
        tabs={[
          { id: "income", label: t("expenses.financials.tab.income"), icon: "ri-line-chart-line" },
          { id: "balance", label: t("expenses.financials.tab.balance"), icon: "ri-scales-3-line" },
          { id: "cashflow", label: t("expenses.financials.tab.cashflow"), icon: "ri-exchange-dollar-line" },
        ]}
      />
      <p className={`text-sm ${c.muted}`}>{t(`expenses.financials.about.${view}`)}</p>

      <ErrorBox message={error === "load" ? t("expenses.errors.load") : error} />
      {!data && !error && <Spinner />}
      {data && data.withoutRate > 0 && <Notice tone="warn">{t("expenses.list.withoutRate", { count: data.withoutRate })}</Notice>}

      {data && view !== "balance" && (
        <div className={c.card} data-testid={`statement-${view}`}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className={`text-sm font-semibold ${c.text}`}>
              {t(`expenses.financials.tab.${view}`)} · {periodLabel(periods[0]?.period || from)} – {periodLabel(periods[periods.length - 1]?.period || range.to)}
            </h3>
            <button
              type="button"
              className={c.secondary}
              onClick={() => downloadText(`${view === "income" ? "estado-de-resultados" : "flujo-de-efectivo"}-${from}_${range.to}.csv`, statementCsv(rows, labels, rowLabel, { concept: t("expenses.financials.concept"), total: t("expenses.financials.total") }))}
            >
              <i className="ri-download-2-line mr-1" />
              CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className={`text-xs ${c.muted}`}>
                  <th className={`sticky left-0 py-1.5 pr-4 text-left font-medium ${isDarkMode ? "bg-dark-card" : "bg-white"}`}>{t("expenses.financials.concept")}</th>
                  {labels.map((label, i) => (
                    <th key={periods[i].period} className="whitespace-nowrap px-3 py-1.5 text-right font-medium">
                      {label}
                    </th>
                  ))}
                  <th className="whitespace-nowrap py-1.5 pl-3 text-right font-semibold">{t("expenses.financials.total")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  if (row.kind === "header") {
                    return (
                      <tr key={row.key}>
                        <td colSpan={labels.length + 2} className={`pb-1 pt-4 text-xs font-semibold uppercase tracking-wide ${c.muted}`}>
                          {rowLabel(row)}
                        </td>
                      </tr>
                    );
                  }
                  const strong = row.kind === "subtotal" || row.kind === "total";
                  const tone = (value: number | null) => (row.kind === "total" && value != null ? (value < 0 ? "text-red-500" : "text-emerald-500") : "");
                  return (
                    <tr key={row.key} data-testid={`row-${row.key}`} className={`${strong ? `border-t-2 ${c.border} font-semibold` : `border-t ${c.border}`} ${row.kind === "memo" ? c.muted : c.text}`}>
                      <td className={`sticky left-0 py-1.5 pr-4 ${row.kind === "line" ? "pl-4" : ""} ${isDarkMode ? "bg-dark-card" : "bg-white"}`}>{rowLabel(row)}</td>
                      {row.values.map((value, i) => (
                        <td key={periods[i].period} className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${tone(value)}`}>
                          {cell(row, value)}
                        </td>
                      ))}
                      <td className={`whitespace-nowrap py-1.5 pl-3 text-right font-semibold tabular-nums ${tone(row.total)}`}>{cell(row, row.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className={`mt-3 text-xs ${c.muted}`}>{t(`expenses.financials.note.${view}`, { months: data.depreciationMonths })}</p>
          {view === "income" && !losses && <p className={`mt-1 text-xs ${c.muted}`}>{t("expenses.financials.lossesUnknown")}</p>}
        </div>
      )}

      {data && b && view === "balance" && (
        <div data-testid="statement-balance" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className={`text-sm font-semibold ${c.text}`}>{t("expenses.financials.balanceAsOf", { date: b.asOf })}</h3>
            <button type="button" className={c.secondary} onClick={openItems}>
              <i className="ri-edit-line mr-1" />
              {t("expenses.financials.manualItems")}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className={c.card}>
              <h4 className={`mb-2 text-xs font-semibold uppercase tracking-wide ${c.muted}`}>{t("expenses.financials.balance.assets")}</h4>
              <table className={`w-full text-sm ${c.text}`}>
                <tbody>
                  <BalanceRow label={t("expenses.financials.balance.cash")} hint={t("expenses.financials.balance.cashHint")} value={m(b.assets.cash)} />
                  <BalanceRow label={t("expenses.financials.balance.equipmentCost")} value={m(b.assets.equipmentCost)} />
                  <BalanceRow label={t("expenses.financials.balance.accumulatedDepreciation")} value={m(b.assets.accumulatedDepreciation === 0 ? 0 : -b.assets.accumulatedDepreciation)} />
                  {manualRows(b.assets.manual)}
                  <BalanceRow strong testId="balance-assets" label={t("expenses.financials.balance.totalAssets")} value={m(b.assets.total)} />
                </tbody>
              </table>
            </div>
            <div className={c.card}>
              <h4 className={`mb-2 text-xs font-semibold uppercase tracking-wide ${c.muted}`}>{t("expenses.financials.balance.liabilities")}</h4>
              <table className={`w-full text-sm ${c.text}`}>
                <tbody>
                  <BalanceRow label={t("expenses.financials.balance.reimbursable")} hint={t("expenses.financials.balance.reimbursableHint")} value={m(b.liabilities.reimbursable)} />
                  <BalanceRow label={t("expenses.financials.balance.accruedPayroll")} hint={t("expenses.financials.balance.accruedPayrollHint")} value={m(b.liabilities.accruedPayroll)} />
                  {manualRows(b.liabilities.manual)}
                  <BalanceRow strong label={t("expenses.financials.balance.totalLiabilities")} value={m(b.liabilities.total)} />
                </tbody>
              </table>
              <h4 className={`mb-2 mt-5 text-xs font-semibold uppercase tracking-wide ${c.muted}`}>{t("expenses.financials.balance.equity")}</h4>
              <table className={`w-full text-sm ${c.text}`}>
                <tbody>
                  {manualRows(b.equity.manual)}
                  <BalanceRow label={t("expenses.financials.balance.retainedEarnings")} value={m(b.equity.retainedEarnings)} />
                  <BalanceRow label={t("expenses.financials.balance.currentEarnings", { year: b.asOf.slice(0, 4) })} value={m(b.equity.currentEarnings)} />
                  {Math.abs(b.equity.unreconciled) >= 0.01 && (
                    <BalanceRow label={t("expenses.financials.balance.unreconciled")} hint={t("expenses.financials.balance.unreconciledHint")} value={m(b.equity.unreconciled)} testId="balance-unreconciled" />
                  )}
                  <BalanceRow strong label={t("expenses.financials.balance.totalEquity")} value={m(b.equity.total)} />
                  <BalanceRow strong testId="balance-liabilities-equity" label={t("expenses.financials.balance.totalLiabilitiesEquity")} value={m(b.liabilities.total + b.equity.total)} />
                </tbody>
              </table>
            </div>
          </div>
          <Notice tone={Math.abs(b.difference) < 0.01 ? "ok" : "warn"}>
            {t(Math.abs(b.difference) < 0.01 ? "expenses.financials.balanced" : "expenses.financials.notBalanced", { assets: m(b.assets.total), other: m(b.liabilities.total + b.equity.total) })}
          </Notice>
          <p className={`text-xs ${c.muted}`}>{t("expenses.financials.note.balance", { months: data.depreciationMonths, since: monthLabel(data.inception, lang) })}</p>
        </div>
      )}

      <Dialog
        wide
        open={Boolean(items)}
        title={t("expenses.financials.manualItems")}
        onClose={() => setItems(null)}
        footer={
          <>
            <button type="button" className={c.secondary} onClick={() => setItems(null)}>
              {t("expenses.cancel")}
            </button>
            <button type="button" className={c.primary} disabled={busy || !itemsValid} onClick={saveItems}>
              {busy ? t("expenses.saving") : t("expenses.save")}
            </button>
          </>
        }
      >
        {items && (
          <div className="space-y-3">
            <p className={`text-sm ${c.muted}`}>{t("expenses.financials.manualHint")}</p>
            <ErrorBox message={itemsError} />
            {items.map((it, i) => {
              const set = (change: Partial<ItemDraft>) => setItems(items.map((row, k) => (k === i ? { ...row, ...change } : row)));
              return (
                <div key={i} className="grid grid-cols-2 items-end gap-2 md:grid-cols-[9rem_1fr_9rem_10rem_auto]" data-testid="balance-item">
                  <Field label={t("expenses.financials.item.section")}>
                    <select className={c.input} value={it.section} onChange={(e) => set({ section: e.target.value as BalanceSection })}>
                      {BALANCE_SECTIONS.map((s) => (
                        <option key={s} value={s}>
                          {t(`expenses.financials.section.${s}`)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t("expenses.financials.item.label")}>
                    <input className={c.input} value={it.label} placeholder={t("expenses.financials.item.labelHint")} onChange={(e) => set({ label: e.target.value })} />
                  </Field>
                  <Field label={t("expenses.fields.amount")}>
                    <input type="number" step="0.01" inputMode="decimal" className={c.input} value={it.amount} onChange={(e) => set({ amount: e.target.value })} />
                  </Field>
                  <Field label={t("expenses.financials.item.since")}>
                    <input type="date" className={c.input} value={it.date} onChange={(e) => set({ date: e.target.value })} />
                  </Field>
                  <button type="button" className={`rounded p-2 ${c.muted} hover:text-red-500`} aria-label={t("expenses.financials.item.remove")} onClick={() => setItems(items.filter((_, k) => k !== i))}>
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              );
            })}
            <button type="button" className={c.secondary} onClick={() => setItems([...items, { section: "activo", label: "", amount: "", date: "" }])}>
              <i className="ri-add-line mr-1" />
              {t("expenses.financials.item.add")}
            </button>
          </div>
        )}
      </Dialog>
    </Page>
  );
}

function BalanceRow({ label, value, hint, tag, strong, testId }: { label: string; value: string; hint?: string; tag?: string; strong?: boolean; testId?: string }) {
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  return (
    <tr className={strong ? `border-t-2 ${c.border} font-semibold` : `border-t ${c.border}`} data-testid={testId}>
      <td className={`py-1.5 ${strong ? "" : "pl-3"}`}>
        {label}
        {tag && <span className={`ml-2 rounded bg-gray-400/15 px-1.5 py-0.5 text-[10px] font-normal ${c.muted}`}>{tag}</span>}
        {hint && <span className={`block text-[11px] font-normal ${c.muted}`}>{hint}</span>}
      </td>
      <td className="whitespace-nowrap py-1.5 text-right tabular-nums">{value}</td>
    </tr>
  );
}
